const unscaledVector = new THREE.Vector3(1, 1, 1);
const posRotMatrix = new THREE.Matrix4();
const inverterMatrix = new THREE.Matrix4();

class TransformModifiers {
    constructor() { }

    createHiddenRoot(rootObject) {

        const bbox = new THREE.Box3().setFromObject(rootObject);
        const bboxSize = bbox.getSize(new THREE.Vector3());
        const bboxMaxSize = 1 / (Math.max(bboxSize.x, bboxSize.y, bboxSize.z) * 0.5);

        const root = new THREE.Object3D();
        root.scale.set(bboxMaxSize, bboxMaxSize, bboxMaxSize);
        root.isHiddenRoot = true;
        // rootObject.scale.divideScalar(bboxMaxSize);

        const parent = rootObject.parent;

        if (parent) {
            parent.add(root);
        }

        root.add(rootObject);

        return root;

    }

    _createMovableParent(object) {
        let movableParent = new THREE.Group();
        movableParent.position.copy(object.position);
        movableParent.quaternion.copy(object.quaternion);
        movableParent.scale.copy(object.scale);
        movableParent.updateMatrix();

        let movableInverters = [];

        {
            let { x, y, z } = object.scale;

            if (x === y && y === z) {
                let movableInverter = new THREE.Group();
                inverterMatrix.getInverse(movableParent.matrix);
                inverterMatrix.decompose(movableInverter.position, movableInverter.quaternion, movableInverter.scale);

                movableInverters.push(movableInverter);
            } else {
                let movableScaleInverter = new THREE.Group();
                movableScaleInverter.scale.set(1/x, 1/y, 1/z);

                let movablePosRotInverter = new THREE.Group();
                posRotMatrix.compose(object.position, object.quaternion, unscaledVector);
                inverterMatrix.getInverse(posRotMatrix);
                inverterMatrix.decompose(movablePosRotInverter.position, movablePosRotInverter.quaternion, movablePosRotInverter.scale);

                movableInverters.push(movableScaleInverter, movablePosRotInverter);
            }
        }

        movableParent.isMovableParent = true;
        for (let movableInverter of movableInverters) {
            movableInverter.isMovableInverter = true;
        }
        object.movableParent = movableParent;

        movableParent.add(movableInverters[0]);
        for (let i = 0; i < movableInverters.length-1; i++) {
            movableInverters[i].add(movableInverters[i+1]);
        }

        return { movableParent, movableInverters };
    }

    addMovableParents(objects) {
        let movableParents = new WeakMap();
        for (let object of objects) {
            movableParents.set(object, this._createMovableParent(object));
        }

        let parentChildren = new Map();
        for (let object of objects) {
            let parent = object.parent;
            if (parent && !parentChildren.has(parent)) {
                let getChild = (child) => {
                    let movable = movableParents.get(child);
                    return movable ? movable.movableParent : child;
                };
                let children = parent.children.map(getChild);
                parentChildren.set(parent, children);
            }
        }

        for (let object of objects) {
            let { movableInverters } = movableParents.get(object);
            movableInverters[movableInverters.length-1].add(object);
        }

        for (let [parent, children] of parentChildren) {
            parent.add(...children);
        }
    }

    addMovableParent(object) {
        let parent = object.parent;

        let { movableParent, movableInverters } = this._createMovableParent(object);

        if (parent) {
            parent.add(movableParent);
        }

        movableInverters[movableInverters.length-1].add(object);

        return movableParent;
    }

    getMovableObject(object) {
        return object.movableParent || object;
    }

    getOrdinaryParent(object) {
        object = object.parent;

        while (object && this.isTransformModifier(object)) {
            object = object.parent;
        }

        return object || null;
    }

    getOrdinaryChildren(object) {
        let children = [];
        this._addOrdinaryChildren(object, children);
        return children;
    }

    _addOrdinaryObjects(object, destArray) {
        if (this.isTransformModifier(object)) {
            this._addOrdinaryChildren(object, destArray);
        } else {
            destArray.push(object);
        }
    }

    _addOrdinaryChildren(object, destArray) {
        for (let child of object.children) {
            this._addOrdinaryObjects(child, destArray);
        }
    }

    isTopLevelOrdinaryObject(object) {
        if (this.isTransformModifier(object)) {
            return false;
        }

        let parent = this.getOrdinaryParent(object);
        return !parent || parent.type === 'Scene';
    }

    getOrdinaryParentUuid(objectUuid, objectsData) {
        let objectData = objectsData[objectUuid];

        while (true) {
            objectUuid = objectData.parent;
            objectData = objectsData[objectUuid];

            if (!objectData || !this.isTransformModifier(objectData)) {
                break;
            }
        }

        return objectData ? objectUuid : null;
    }

    isTopLevelOrdinaryObjectData(objectUuid, objectsData) {
        if (this.isTransformModifier(objectsData[objectUuid])) {
            return false;
        }

        let parentUuid = this.getOrdinaryParentUuid(objectUuid, objectsData);
        return !parentUuid;
    }

    applyFlagsFromData(objectData, object) {
        if (objectData.isHiddenRoot) {
            object.isHiddenRoot = true;
            object.visible = true;
        }

        if (objectData.isMovableParent) {
            object.isMovableParent = true;
            object.visible = true;
        }

        if (objectData.isMovableInverter) {
            object.isMovableInverter = true;
            object.visible = true;
        }
    }

    applyMovableParentFromData(objectData, object, objectUuidArray) {
        let uuid = objectData.movableParent;
        if (uuid) {
            const found = objectUuidArray.find(o => o.uuid === uuid);

            if (found) object.movableParent = found.object;
        }
    }

    isTransformModifier(object) {
        return !!(object.isHiddenRoot || object.isMovableParent || object.isMovableInverter);
    }
}

export default new TransformModifiers();
